import React from 'react';
import { fg } from '@atlassian/jira-feature-gating';
import { forgeProjectModuleResource } from '@atlassian/jira-navigation-apps-resources/src/controllers/forge/index.tsx';
import { staticProjectSidebarConnectAddonsNavigationResource } from '@atlassian/jira-navigation-apps-resources/src/controllers/sidebar/static-project-sidebar-connect-addons/index.tsx';
import { staticProjectSidebarNavigationResource } from '@atlassian/jira-navigation-apps-resources/src/controllers/sidebar/static-project-sidebar/index.tsx';
import { staticSidebarShortcutsResource } from '@atlassian/jira-navigation-apps-resources/src/controllers/sidebar/static-sidebar-shortcuts/index.tsx';
import { useJiraSoftwareProjectNavigation } from '@atlassian/jira-navigation-apps-sidebar-common/src/controllers/common/project-sidebar-navigation-resource/index.tsx';
import {
	getNavigationResources,
	getNavigationSidebarProjectSettingsResource,
} from '@atlassian/jira-router-resources-navigation/src/index.tsx';
import { projectContextResource } from '@atlassian/jira-router-resources-project-context/src/services/project-context/index.tsx';
import { incidentExperimentTraitResource } from '@atlassian/jira-router-resources-software-incidents/src/services/index.tsx';

import { Redirect, type RouteContext } from '@atlassian/react-resource-router';

export const SOFTWARE_PRODUCT = 'software';

// This URL path is passed to the SPA router with ':projectKey' as a path param.
export const projectSettingsUrl = (product: string) =>
	`/jira/${product}/projects/:projectKey/settings`;

/**
 * Get the appropriate settings resources based on the product.
 */
export const getSettingsNavigationResources = () => {
	return [
		...getNavigationResources(),
		// these have been expanded from getNavigationSidebarProjectResource - please examine whether your route requires these resources in nav4 and remove them with the getWillShowNav4 feature gate if it does not
		incidentExperimentTraitResource,
		projectContextResource,
		forgeProjectModuleResource,
		staticSidebarShortcutsResource,
		staticProjectSidebarNavigationResource,
		staticProjectSidebarConnectAddonsNavigationResource,
		// end of resources expanded from getNavigationSidebarProjectResource
		...getNavigationSidebarProjectSettingsResource(),
	];
};

export const RedirectForSettings = ({ match }: RouteContext): React.JSX.Element | null => {
	const { data, loading } = useJiraSoftwareProjectNavigation();
	// Wait for nav data to be available before deciding where to redirect on cold loads
	if (loading || !data) {
		return null;
	}
	const { projectKey } = match.params;

	if (
		data?.permissionInfo?.isAdmin ||
		data?.permissionInfo?.isSiteAdmin ||
		data?.permissionInfo?.projectPermissionsQueried?.ADMINISTER_PROJECTS
	) {
		return (
			<Redirect
				to={`/jira/software/projects/${String(match.params.projectKey)}/settings/details`}
			/>
		);
	}

	// the next three if branches do not make a lot of sense for TMP as you always a project admin in TMP
	// however a wrong link is generated by the nav4 navbar for CMP projects which matches to this redirect logic
	// ex: "/jira/software/projects/JSWCMP1/settings"
	// instead it should be "/jira/software/c/projects/JSWCMP1/settings"
	// which matches to CMP redirect logic defined in RedirectForSettingsForClassicProject
	if (data?.permissionInfo?.projectPermissionsQueried?.EDIT_WORKFLOW === true) {
		return <Redirect to={`/plugins/servlet/project-config/${String(projectKey)}/workflows`} />;
	}
	if (data?.permissionInfo?.projectPermissionsQueried?.EDIT_ISSUE_LAYOUT === true) {
		return <Redirect to={`/plugins/servlet/project-config/${String(projectKey)}/issuelayout`} />;
	}
	if (fg('vmc-11-manage-versions-permission')) {
		if (data?.permissionInfo?.projectPermissionsQueried?.MANAGE_VERSIONS === true) {
			return (
				<Redirect
					to={`/plugins/servlet/project-config/${String(projectKey)}/administer-versions`}
				/>
			);
		}
	}

	return (
		<Redirect to={`/jira/software/projects/${String(match.params.projectKey)}/settings/details`} />
	);
};

export const RedirectForSettingsForClassicProject = ({
	match,
}: RouteContext): React.JSX.Element | null => {
	const { data, loading } = useJiraSoftwareProjectNavigation();
	// Wait for nav data to be available before deciding where to redirect on cold loads
	if ((loading || !data) && fg('vmc-52-settings-redirect-wait-till-data-received')) {
		return null;
	}
	const { projectKey } = match.params;

	if (
		data?.permissionInfo?.isAdmin ||
		data?.permissionInfo?.isSiteAdmin ||
		data?.permissionInfo?.projectPermissionsQueried?.ADMINISTER_PROJECTS
	) {
		return <Redirect to={`/jira/software/c/projects/${String(projectKey)}/settings/details`} />;
	}
	if (data?.permissionInfo?.projectPermissionsQueried?.EDIT_WORKFLOW === true) {
		return <Redirect to={`/plugins/servlet/project-config/${String(projectKey)}/workflows`} />;
	}
	if (data?.permissionInfo?.projectPermissionsQueried?.EDIT_ISSUE_LAYOUT === true) {
		return <Redirect to={`/plugins/servlet/project-config/${String(projectKey)}/issuelayout`} />;
	}
	if (fg('vmc-11-manage-versions-permission')) {
		if (data?.permissionInfo?.projectPermissionsQueried?.MANAGE_VERSIONS === true) {
			return (
				<Redirect
					to={`/plugins/servlet/project-config/${String(projectKey)}/administer-versions`}
				/>
			);
		}
	}

	return <Redirect to={`/jira/software/c/projects/${String(projectKey)}/settings/details`} />;
};
